/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears
} from "date-fns";
import cx from "classnames";

import styles from "./date-range-picker.module.scss";
import { Button } from "../Button/Button";
import { Card } from "../Card/Card";
import { Picker } from "../types";
import { InputControls } from "./InputControls";
import {
  dateFormat,
  dayOffset,
  isAfter,
  isBefore,
  isSameDay,
  pickerContext,
  toDate
} from "../utils";
import { MiniCalendar } from "../MiniCalendar/MiniCalendar";
import { CalendarControls } from "../MiniCalendar/CalendarControls";

export enum DaysOfWeek {
  sunday = 0,
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6
}

export namespace DateRangePicker {
  export type Range = {
    end: Picker.ISODateString;
    name: string;
    start: Picker.ISODateString;
  };

  export type Props = Picker.BaseProps & {
    actionsRowDivider?: boolean;
    applyButton?: React.ReactElement;
    endDate: Picker.ISODateString;
    onUpdate(
      startDate: Picker.ISODateString,
      endDate: Picker.ISODateString
    ): void;
    ranges?: Range[];
    startDate: Picker.ISODateString;
    inputControlsPosition?: "vertical" | "horizontal";
  };
}

export const DateRangePicker = (props: DateRangePicker.Props): JSX.Element => {
  const {
    actionsRowDivider = false,
    applyButton: ApplyButton,
    inputControlsPosition = "horizontal",
    locale,
    firstDayOfWeek = DaysOfWeek[locale?.options?.weekStartsOn || 0] as string,
    today = format(new Date(), dateFormat)
  } = props;

  const yesterday: Picker.ISODateString = format(
    subDays(toDate(today), 1),
    dateFormat
  );
  const lastWeek = startOfWeek(subWeeks(toDate(today), 1), {
    locale,
    weekStartsOn: dayOffset(firstDayOfWeek.toString())
  });
  const lastMonth = startOfMonth(subMonths(toDate(today), 1));
  const lastYear = startOfYear(subYears(toDate(today), 1));

  const {
    endDate,
    onCancel,
    onUpdate,
    startDate,
    ranges = [
      { end: today, name: "Today", start: today },
      { end: yesterday, name: "Yesterday", start: yesterday },
      {
        end: today,
        name: "Week to Date",
        start: format(
          startOfWeek(toDate(today), {
            locale,
            weekStartsOn: dayOffset(firstDayOfWeek.toString())
          }),
          dateFormat
        )
      },
      {
        end: format(
          endOfWeek(lastWeek, {
            locale,
            weekStartsOn: dayOffset(firstDayOfWeek.toString())
          }),
          dateFormat
        ),
        name: "Last Week",
        start: format(lastWeek, dateFormat)
      },
      {
        end: today,
        name: "Month to Date",
        start: format(startOfMonth(toDate(today)), dateFormat)
      },
      {
        end: format(endOfMonth(lastMonth), dateFormat),
        name: "Last Month",
        start: format(lastMonth, dateFormat)
      },
      {
        end: today,
        name: "Year to Date",
        start: format(startOfYear(toDate(today)), dateFormat)
      },
      {
        end: format(endOfYear(lastYear), dateFormat),
        name: "Last Year",
        start: format(lastYear, dateFormat)
      }
    ],
    rtl = false
  } = props;

  const [hasSelection, setHasSelection] = useState<boolean>(true);
  const [tempStartDate, setTempStartDate] = useState<Picker.ISODateString>(
    startDate
  );
  const [tempEndDate, setTempEndDate] = useState<Picker.ISODateString>(endDate);
  const [visibleMonths, setVisibleMonths] = useState<Picker.ISODateString[]>([
    format(subMonths(toDate(tempEndDate), 1), dateFormat),
    tempEndDate
  ]);

  useEffect((): void => {
    if (hasSelection) {
      setVisibleMonths([
        format(subMonths(toDate(tempEndDate), 1), dateFormat),
        tempEndDate
      ]);
    }
  }, [tempEndDate, hasSelection]);

  const contextValue = {
    firstDayOfWeek,
    locale,
    hasSelection,
    isEndDate: (date: Picker.ISODateString): boolean => {
      if (!hasSelection) {
        if (isAfter(tempEndDate, tempStartDate)) {
          return isSameDay(date, tempEndDate);
        }

        return isSameDay(date, tempStartDate);
      }

      return isSameDay(date, tempEndDate);
    },
    isStartDate: (date: Picker.ISODateString): boolean => {
      if (!hasSelection && isBefore(tempEndDate, tempStartDate)) {
        return isSameDay(date, tempEndDate);
      }

      return isSameDay(date, tempStartDate);
    },
    onNextMonth: async (): Promise<void> => {
      setVisibleMonths(
        visibleMonths.map(
          (date: Picker.ISODateString): Picker.ISODateString =>
            format(addMonths(toDate(date), 1), dateFormat)
        )
      );
    },
    onPrevMonth: async (): Promise<void> => {
      setVisibleMonths(
        visibleMonths.map(
          (date: Picker.ISODateString): Picker.ISODateString =>
            format(subMonths(toDate(date), 1), dateFormat)
        )
      );
    },
    onRangeChange: (start: Picker.ISODateString, end: Picker.ISODateString) => {
      setTempStartDate(start);
      setTempEndDate(end);
      setHasSelection(true);
    },
    onUpdate,
    isWithinSelection: (date: Picker.ISODateString): boolean => {
      let start: string = tempStartDate;
      let end: string = tempEndDate;

      if (isBefore(end, start)) {
        start = tempEndDate;
        end = tempStartDate;
      }

      return isWithinInterval(toDate(date), {
        start: toDate(start),
        end: toDate(end)
      });
    },
    reset: () => {
      contextValue.onRangeChange(startDate, endDate);
      setVisibleMonths([
        format(subMonths(toDate(endDate), 1), dateFormat),
        endDate
      ]);
    },
    rtl,
    tempEndDate,
    tempStartDate
  };

  const onSelection = (date: Picker.ISODateString): void => {
    if (hasSelection) {
      setTempStartDate(date);
      setTempEndDate(date);
      setHasSelection(false);

      return;
    }

    if (isBefore(date, tempStartDate)) {
      contextValue.onRangeChange(date, tempStartDate);

      return;
    }

    contextValue.onRangeChange(tempStartDate, date);
  };

  const onHover = (date: Picker.ISODateString): void => {
    if (!hasSelection) setTempEndDate(date);
  };

  const calendarLabel = useMemo(() => {
    const weekStartsOn = dayOffset(firstDayOfWeek.toString());

    return `${format(toDate(visibleMonths[rtl ? 1 : 0]), "MMMM", {
      locale,
      weekStartsOn
    })} - ${format(toDate(visibleMonths[rtl ? 0 : 1]), "MMMM yyyy", {
      locale,
      weekStartsOn
    })}`;
  }, [visibleMonths, locale, rtl, firstDayOfWeek]);

  return (
    <pickerContext.Provider value={contextValue}>
      <div
        aria-label="Use arrow and page keys to navigate, and press space to select a day"
        className={styles.dateRangePicker}
      >
        <div className={styles.flexRow}>
          <div className={styles.flexCol}>
            {inputControlsPosition === "vertical" ? (
              <InputControls
                position={inputControlsPosition}
                endDate={tempEndDate}
                startDate={tempStartDate}
              />
            ) : null}
            <Card padding={0}>
              <CalendarControls label={calendarLabel} />
              <div className={styles.flexRow}>
                {visibleMonths.map(
                  (date: Picker.ISODateString): JSX.Element => (
                    <MiniCalendar
                      {...props}
                      date={date}
                      key={date}
                      onHover={onHover}
                      onSelection={onSelection}
                      today={today}
                    />
                  )
                )}
              </div>
            </Card>
          </div>
          {ranges.length || inputControlsPosition === "horizontal" ? (
            <div className={cx(styles.flexCol, styles.ml6)}>
              {inputControlsPosition === "horizontal" ? (
                <InputControls
                  position={inputControlsPosition}
                  endDate={tempEndDate}
                  startDate={tempStartDate}
                />
              ) : null}
              <div className={(styles.flexRow, styles.rangesWrapper)}>
                {ranges.map(
                  (range: DateRangePicker.Range): JSX.Element => (
                    <div className={styles.rangeWrapper} key={range.name}>
                      <Button
                        className={styles.rangeButton}
                        padding="condensed"
                        theme="tertiary"
                        onClick={() => {
                          contextValue.onRangeChange(range.start, range.end);
                        }}
                      >
                        {range.name}
                      </Button>
                    </div>
                  )
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.actionsRow}>
          {actionsRowDivider ? (
            <div className={styles.actionsRowDivider} />
          ) : null}
          <Button
            theme="tertiary-transparent"
            onClick={() => {
              contextValue.reset();
              onCancel();
            }}
            padding="condensed"
          >
            Cancel
          </Button>
          {ApplyButton ? (
            React.cloneElement(ApplyButton, {
              disabled:
                !hasSelection ||
                (isSameDay(endDate, tempEndDate) &&
                  isSameDay(startDate, tempStartDate)),
              onClick: () => onUpdate(tempStartDate, tempEndDate)
            })
          ) : (
            <Button
              className={styles.ml2}
              disabled={
                !hasSelection ||
                (isSameDay(endDate, tempEndDate) &&
                  isSameDay(startDate, tempStartDate))
              }
              onClick={() => onUpdate(tempStartDate, tempEndDate)}
              padding="condensed"
              theme="secondary"
            >
              Update
            </Button>
          )}
        </div>
      </div>
    </pickerContext.Provider>
  );
};
